<dialog
  #dialog
  id="audit-assistant"
  class="app-dialog audit-assistant"
  [class.audit-assistant--closing]="closing()"
  aria-labelledby="audit-assistant-title"
  (cancel)="cancel($event)"
  (click)="backdropClick($event)"
>
  <header class="audit-assistant__header">
    <span class="audit-assistant__avatar" aria-hidden="true">
      <span class="fa-solid fa-wand-magic-sparkles"></span>
    </span>
    <div class="audit-assistant__heading">
      <h2 id="audit-assistant-title">{{ i18n.t('auditAssistant.title') }}</h2>
      <p>{{ i18n.t('auditAssistant.context') }}</p>
    </div>
    <div #conversationMenu class="audit-assistant__menu" (keydown)="menuKeydown($event)" (focusout)="menuFocusOut($event)">
      <button class="app-icon-button app-icon-button--round" type="button" (click)="toggleMenu()"
        aria-haspopup="menu" [attr.aria-expanded]="menuOpen()" aria-controls="audit-conversation-menu"
        [attr.aria-label]="i18n.t('auditAssistant.conversationMenu')" [title]="i18n.t('auditAssistant.conversationMenu')">
        <span class="fa-solid fa-ellipsis" aria-hidden="true"></span>
      </button>
      @if (menuOpen()) {
        <div id="audit-conversation-menu" class="app-action-menu audit-assistant__menu-panel" role="menu"
          [attr.aria-label]="i18n.t('auditAssistant.conversationMenu')">
          <button class="app-action-menu__item" type="button" role="menuitem" tabindex="-1" (click)="newConversation()">
            <span class="fa-solid fa-plus" aria-hidden="true"></span>
            <span>{{ i18n.t('auditAssistant.newConversation') }}</span>
          </button>
          @if (menuLoading()) {
            <p class="app-action-menu__label" role="status">{{ i18n.t('auditAssistant.historyLoading') }}</p>
          } @else if (menuError()) {
            <p class="app-action-menu__label" role="alert">{{ i18n.t('auditAssistant.conversationsError') }}</p>
            <button class="app-action-menu__item" type="button" role="menuitem" tabindex="-1" (click)="loadConversations()">
              {{ i18n.t('auditAssistant.historyRetry') }}
            </button>
          } @else if (recentConversations().length) {
            <div class="app-action-menu__separator" role="separator"></div>
            <p class="app-action-menu__label" role="presentation">{{ i18n.t('auditAssistant.previousConversations') }}</p>
            @for (item of recentConversations(); track item.id) {
              <button class="app-action-menu__item" type="button" role="menuitem" tabindex="-1"
                [title]="item.title" (click)="selectConversation(item.id)">
                <span class="fa-solid fa-comment" aria-hidden="true"></span>
                <span class="audit-assistant__menu-description">
                  <span class="audit-assistant__menu-title">{{ item.title }}</span>
                  <span class="audit-assistant__menu-date">{{ conversationDate(item.updated_at) }}</span>
                </span>
              </button>
            }
          }
        </div>
      }
    </div>
    <button class="app-icon-button audit-assistant__close" type="button"
      [attr.aria-label]="i18n.t('auditAssistant.close')" (click)="close()">
      <span class="fa-solid fa-xmark" aria-hidden="true"></span>
    </button>
  </header>

  <section #conversation class="audit-assistant__conversation" (scroll)="onScroll()" [attr.aria-label]="i18n.t('auditAssistant.responses')">
    @if (historyLoading()) {
      <p role="status">{{ i18n.t('auditAssistant.historyLoading') }}</p>
    }
    @if (historyError()) {
      <p role="alert">{{ i18n.t('auditAssistant.historyError') }}</p>
      <button class="app-button" type="button" (click)="loadHistory()">{{ i18n.t('auditAssistant.historyRetry') }}</button>
    } @else if (nextBefore()) {
      <button class="app-button" type="button" [disabled]="historyLoading() || busy()" (click)="loadHistory()">
        {{ i18n.t('auditAssistant.historyOlder') }}
      </button>
    }
    @for (turn of history(); track turn.id) {
      <div class="audit-assistant__question">{{ turn.user_message }}</div>
      <div class="audit-assistant__response">
        <div class="audit-assistant__author">
          <span class="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></span> Juno AI
        </div>
        @if (turn.presentation_json; as searchResults) {
          <app-audit-assistant-results [results]="searchResults" />
        }
        <app-markdown [content]="turn.assistant_message" />
        @if (turn.checklist_result_json; as checklist) {
          <a class="audit-assistant__checklist-link" [href]="'#checklist-' + checklist.checklist_id"
            [attr.aria-disabled]="busy()" [attr.tabindex]="busy() ? -1 : 0"
            [title]="i18n.t('auditAssistant.checklist.edit')"
            (click)="$event.preventDefault(); editChecklist(checklist)">
            <span class="fa-solid fa-clipboard-check" aria-hidden="true"></span>
            <span>{{ checklist.title }}</span>
          </a>
        }
        @if (turn.status !== 'completed' || turn.refused) {
          <p class="audit-assistant__status">{{ i18n.t('auditAssistant.status.' + historyStatus(turn)) }}</p>
        }
      </div>
    }
    @if (question()) {
      <div class="audit-assistant__question">{{ question() }}</div>
      <div class="audit-assistant__response">
        <div class="audit-assistant__author">
          <span class="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></span> Juno AI
        </div>
        @if (results(); as searchResults) {
          <app-audit-assistant-results [results]="searchResults" />
        }
        <app-markdown [content]="responseMarkdown()" />
        @if (createdChecklist(); as checklist) {
          <a class="audit-assistant__checklist-link" [href]="'#checklist-' + checklist.checklist_id"
            [attr.aria-disabled]="busy()" [attr.tabindex]="busy() ? -1 : 0"
            [title]="i18n.t('auditAssistant.checklist.edit')"
            (click)="$event.preventDefault(); editChecklist(checklist)">
            <span class="fa-solid fa-clipboard-check" aria-hidden="true"></span>
            <span>{{ checklist.title }}</span>
          </a>
        }
        @if (progress() && !(createdChecklist() && progress() === 'completed')) {
          <p class="audit-assistant__status" role="status" aria-live="polite">
            @if (busy()) { <span class="fa-solid fa-spinner fa-spin" aria-hidden="true"></span> }
            <span>
              {{ i18n.t('auditAssistant.status.' + progress()) }}
              @if (busy() && checklistStep()) { <span>({{ checklistStep() }}/4)</span> }
            </span>
          </p>
        }
        @if (busy()) {
          <p class="audit-assistant__elapsed" role="timer" aria-live="off">
            {{ i18n.t('auditAssistant.progress.elapsed') }} {{ elapsedTime() }}
          </p>
          @if (progress() === 'checklist_generating') {
            <p class="audit-assistant__progress-note">{{ i18n.t('auditAssistant.progress.checklistHint') }}</p>
          }
        }
        @if (errorKey()) {
          <p class="audit-assistant__error" role="alert">{{ i18n.t(errorKey()) }}</p>
        }
        @if (results() && (errorKey() || progress() === 'stopped' || progress() === 'incomplete')) {
          <p class="audit-assistant__status">{{ i18n.t('auditAssistant.results.summaryInterrupted') }}</p>
        }
      </div>
    } @else if (!history().length && !historyLoading() && !historyError()) {
      <div class="audit-assistant__welcome">
        <app-markdown [content]="i18n.t('auditAssistant.welcome')" />
      </div>
      <div class="audit-assistant__examples">
        <p class="audit-assistant__examples-label">{{ i18n.t('auditAssistant.examples') }}</p>
        <p><span class="fa-solid fa-magnifying-glass" aria-hidden="true"></span>{{ i18n.t('auditAssistant.exampleSearch') }}</p>
        <p><span class="fa-solid fa-clipboard-list" aria-hidden="true"></span>{{ i18n.t('auditAssistant.exampleCreate') }}</p>
      </div>
    }
  </section>

  <footer class="audit-assistant__footer">
    <label class="audit-assistant__label" for="audit-assistant-message">{{ i18n.t('auditAssistant.message') }}</label>
    <div class="audit-assistant__composer">
      <textarea id="audit-assistant-message" class="app-textarea audit-assistant__input"
        rows="3" maxlength="10000" autofocus
        [placeholder]="i18n.t('auditAssistant.placeholder')"
        [value]="draft()" (input)="draft.set($any($event.target).value)" (keydown)="keydown($event)"
        aria-describedby="audit-assistant-note"></textarea>
      <button class="app-icon-button app-icon-button--send audit-assistant__send" type="button"
        [disabled]="historyLoading() || historyError() || (!busy() && !draft().trim())" (click)="send()"
        [attr.aria-label]="i18n.t(busy() ? 'auditAssistant.stop' : 'auditAssistant.send')"
        [title]="i18n.t(busy() ? 'auditAssistant.stop' : 'auditAssistant.send')">
        <span class="fa-solid" [class.fa-square]="busy()" [class.fa-arrow-up]="!busy()" aria-hidden="true"></span>
      </button>
    </div>
    <p id="audit-assistant-note" class="audit-assistant__note">{{ i18n.t('auditAssistant.note') }}</p>
  </footer>
</dialog>
